import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Trash2, PoundSterling, Mail } from "lucide-react";

const gbp = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BADGE = {
  paid: ["Paid", "text-emerald-700 bg-emerald-50"],
  part_paid: ["Part paid", "text-amber-700 bg-amber-50"],
  unpaid: ["Unpaid", "text-rose-700 bg-rose-50"],
};

export function PaymentBadge({ inv }) {
  const [label, cls] = BADGE[inv.payment_status] || BADGE.unpaid;
  return <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${cls}`} data-testid={`invoice-pay-status-${inv.id}`}>{label}{inv.payment_status === "part_paid" ? ` · ${gbp(inv.balance)} due` : ""}</span>;
}

export function OwedSummary({ summary }) {
  if (!summary) return null;
  const cell = (label, v, tone, tid) => (
    <div className="flex-1 px-5 py-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-sm font-bold ${tone}`} data-testid={tid}>{gbp(v)}</div>
    </div>
  );
  return (
    <div className="flex divide-x divide-slate-100 border-b border-slate-100 bg-slate-50/60" data-testid="invoice-owed-summary">
      {cell("Invoiced", summary.invoiced, "text-slate-900", "invoice-summary-invoiced")}
      {cell("Paid", summary.paid, "text-emerald-600", "invoice-summary-paid")}
      {cell("Still owed", summary.outstanding, summary.outstanding > 0 ? "text-rose-600" : "text-slate-900", "invoice-summary-outstanding")}
      {summary.held_deposits > 0 && cell("Deposit held (not yet invoiced)", summary.held_deposits, "text-blue-600", "invoice-summary-held")}
    </div>
  );
}

export function PaymentsButton({ legacyId, inv, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: "", date: "", method: "", note: "" });
  const [busy, setBusy] = useState(false);

  const add = async (full) => {
    setBusy(true);
    try {
      const body = full ? { method: f.method, note: f.note, date: f.date || null }
        : { amount: Number(f.amount), method: f.method, note: f.note, date: f.date || null };
      await api.post(`/tasks/${legacyId}/invoices/${inv.id}/payments`, body);
      toast.success(full ? `${inv.invoice_no} marked as paid` : "Payment recorded");
      setF({ amount: "", date: "", method: "", note: "" });
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not record payment"); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    try { await api.delete(`/tasks/${legacyId}/invoices/${inv.id}/payments/${p.id}`); toast.success("Payment removed"); onChanged(); }
    catch { toast.error("Could not remove payment"); }
  };
  const receipt = async (p) => {
    let def = "";
    try { def = (await api.get(`/tasks/${legacyId}/customer-email`)).data.email || ""; } catch { /* none */ }
    const to = window.prompt("Send payment receipt to:", p.receipt_sent_to || def);
    if (!to) return;
    try { const r = await api.post(`/tasks/${legacyId}/invoices/${inv.id}/payments/${p.id}/receipt-email`, { to: to.trim() }); toast.success(`Receipt emailed to ${r.data.to}`); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send receipt"); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-slate-400 hover:text-emerald-600 shrink-0" title="Payments" data-testid={`invoice-payments-${inv.id}`}>
        <PoundSterling className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invoice-payments-dialog">
          <DialogHeader><DialogTitle>Payments · {inv.invoice_no}</DialogTitle></DialogHeader>
          <div className="mt-3 space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3 text-center">
              <div><div className="text-[10px] uppercase text-slate-400">Total</div><b>{gbp(inv.total)}</b></div>
              <div><div className="text-[10px] uppercase text-slate-400">Paid</div><b className="text-emerald-600">{gbp(inv.amount_paid)}</b></div>
              <div><div className="text-[10px] uppercase text-slate-400">Balance</div><b className={inv.balance > 0 ? "text-rose-600" : ""} data-testid="invoice-payments-balance">{gbp(inv.balance)}</b></div>
            </div>
            {(inv.payments || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 pb-1.5" data-testid={`invoice-payment-${p.id}`}>
                <span>{p.date} · <b>{gbp(p.amount)}</b>{p.method ? ` · ${p.method}` : ""}{p.note ? ` · ${p.note}` : ""}<span className="block text-[11px] text-slate-400">by {p.recorded_by}{p.receipt_sent_to ? ` · receipt sent to ${p.receipt_sent_to}` : ""}</span></span>
                <div className="flex items-center gap-2">
                  <button onClick={() => receipt(p)} className="text-slate-400 hover:text-indigo-600" title="Email receipt to customer" data-testid={`invoice-payment-receipt-${p.id}`}><Mail className="w-4 h-4" /></button>
                  <button onClick={() => remove(p)} className="text-slate-300 hover:text-rose-500" data-testid={`invoice-payment-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {inv.balance > 0 && (
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="0.01" placeholder={`Amount (max ${gbp(inv.balance)})`} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="invoice-payment-amount" />
                <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="invoice-payment-date" />
                <Input placeholder="Method (card, BACS…)" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="invoice-payment-method" />
                <Input placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="invoice-payment-note" />
              </div>
            )}
          </div>
          {inv.balance > 0 && (
            <DialogFooter>
              <Button variant="outline" disabled={busy || !(Number(f.amount) > 0)} onClick={() => add(false)} data-testid="invoice-payment-add">Record part payment</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => add(true)} data-testid="invoice-mark-paid">
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Mark fully paid
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
